import { useCallback, useMemo, useState } from "react";

import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { AuthenticatorCodeValidator } from "@/Common/Validators/AuthenticatorCodeValidator";
import { Button, LinkButton } from "@/DesignSystem";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { NewExperienceLoginClient } from "../Api/NewExperienceLoginClient";

import styles from "./NewExperienceLoginCredentialsView.module.scss";

interface ILoginAuthenticatorCodeViewProps {
    loginPageModel: ILoginModel;
    stringProvider: ResourceProvider;
    errorMessageJsx?: JSX.Element;
    loginOperation: LocalOperations.Login;
}

export function NewExperienceLoginAuthenticatorView({
    loginPageModel,
    stringProvider,
    errorMessageJsx,
    loginOperation
}: Readonly<ILoginAuthenticatorCodeViewProps>): JSX.Element {
    const [sendByEmailLinkDisabled, setSendByEmailLinkDisabled] = useState(false);
    const { getText } = useTranslation(stringProvider);
    const authenticatorCodeValidator = useMemo(() => new AuthenticatorCodeValidator(), []);

    const isValidatingCredentials = loginPageModel.get("ValidatingCredentials");
    const isLoggedIn = loginPageModel.get("LoggedIn");
    const authenticatorCode = loginPageModel.get("AuthenticatorCode")?.trim() || "";
    const codeLength = authenticatorCode.length;
    const isAuthenticatorCodeValid = authenticatorCodeValidator.validate(authenticatorCode);
    const errorMessage = loginPageModel.get("ErrorMessage").Message;
    const isInvalid = errorMessage || codeLength !== AuthenticatorCodeValidator.codeLength;
    const inputFormCssClass = `form-control ${isInvalid ? "invalid" : ""} ${codeLength !== 0 ? styles.letterSpace : ""}`;

    const showCodeInput = !isValidatingCredentials && !isLoggedIn;
    const showSendCodeByEmailLink = showCodeInput && !cacheStore.ClinicSettings.EnableAppOnlyMultifactorAuthentication;

    const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (!isValidatingCredentials && authenticatorCode && event.key === "Enter") {
            sessionStore.dispatch(loginOperation);
        }
    }, [isValidatingCredentials, authenticatorCode, loginOperation]);

    const handleAuthenticatorCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, AuthenticatorCodeValidator.codeLength);
        sessionStore.dispatch(LocalOperations.Login.AuthenticatorCode_TextChange, { Value: value });
    }, []);

    const renderVisualCode = (): string => {
        if (!authenticatorCode) {
            return null;
        }

        const visualArray = Array.from({ length: AuthenticatorCodeValidator.codeLength }, (_, i) => authenticatorCode[i] || "_");
        return visualArray.join("");
    };

    const sendCodeByEmail = useCallback(async (): Promise<void> => {
        try {
            const response = await NewExperienceLoginClient.sendAuthenticationCodeByEmail();
            if (!response) {
                displayToast(MessageToast.Error, getText("InternalServerError"));
                return;
            }

            displayToast(MessageToast.Success, getText("CodeSentLabel"), {
                hideProgressBar: false,
                pauseOnFocusLoss: false,
                autoClose: 3000,
                pauseOnHover: false,
                closeOnClick: false
            });
        }
        catch {
            displayToast(MessageToast.Error, getText("InternalServerError"));
        }
    }, [getText]);

    const handleSendCodeByEmail = useCallback(async (): Promise<void> => {
        if (sendByEmailLinkDisabled)
            return;

        setSendByEmailLinkDisabled(true);
        await sendCodeByEmail();
        setTimeout(() => setSendByEmailLinkDisabled(false), 3000);
    }, [sendByEmailLinkDisabled, sendCodeByEmail]);

    useMount(() => {
        if (
            cacheStore.ClinicSettings?.EnableMultifactorAuthenticationByEmail &&
            sessionStore.clientLoginModel.get("SelectedAuthenticatorCodeDeliveryMethod") === AuthenticatorCodeDeliveryMethod.Email
        ) {
            sendCodeByEmail();
        }
    });

    return (
        <>
            <div className={styles.loginRow}>
                <Label
                    htmlFor="user_name"
                    label={getText("NewExperienceLogin_AuthenticatorDigitCodelabel")}
                />
                {showCodeInput && (
                    <div className={styles.authenticatorCodeContainer}>
                        <div className={`${styles.visualCode} ${inputFormCssClass}`}>
                            {renderVisualCode()}
                        </div>
                        <input
                            type="text"
                            id="NewExperienceAuthenticatorCode"
                            data-cy="new-experience-login-authenticator-code-text-input"
                            onKeyUp={handleKeyPress}
                            className={`${styles.authenticatorCode} ${inputFormCssClass}`}
                            maxLength={AuthenticatorCodeValidator.codeLength}
                            placeholder={getText("AuthenticatorCodePlaceholder")}
                            onChange={handleAuthenticatorCodeChange}
                            value={authenticatorCode}
                            autoComplete="off"
                            autoFocus
                        />
                    </div>
                )}
            </div>

            {errorMessageJsx}

            <div className={styles.loginButtonContainer}>
                <Button
                    variant="brand"
                    disabled={!isAuthenticatorCodeValid || isValidatingCredentials || isLoggedIn}
                    id="NewExperienceLoginVerifyButton"
                    dataCy="new-experience-login-authenticator-verify-button"
                    onClick={() => sessionStore.dispatch(loginOperation)}
                    label={getText("NewExperienceLogin_VerifyCodeLabel")}
                    size="lg"
                />
                {showSendCodeByEmailLink && (
                    <div className={styles.sendByEmailLink}>
                        <LinkButton
                            label={getText("NewExperienceLogin_SendByEmailLabel")}
                            onClick={handleSendCodeByEmail}
                            disabled={sendByEmailLinkDisabled}
                            dataCy="new-experience-login-authenticator-send-by-email-link"
                        />
                    </div>
                )}
            </div>
        </>
    );
}
